import { FormControl, FormErrorMessage, Select, Switch } from '@chakra-ui/react'
import type { Address, FindAddressResult } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { useFindAddress, useFindKatUzemi, useFindStreet } from '../hooks'
import { useNameof } from '../hooks/useNameof'
import { Autocomplete } from './Autocomplete'
import { FormGroup } from './FormGroup'
import { FormLabel } from './FormLabel'
import { InputGroup } from './InputGroup'
import { SwitchControl } from './SwitchControl'

export type RuianAddressSelectorProps = {
  addressPath: string
  isDisabled?: boolean
}

const RuianAddressSelector = ({ addressPath, isDisabled }: RuianAddressSelectorProps) => {
  const nameof = useNameof<Address>(addressPath)
  //Address
  const { setValue, getValues, register, getFieldState, watch } = useFormContext()

  const addressPointName = nameof((x) => x.addressPoint)
  const streetName = nameof((x) => x.street)
  const streetNumberName = nameof((x) => x.streetNumber)
  const cityWithCityPartName = nameof((x) => x.cityWithCityPart)
  const postCodeName = nameof((x) => x.postCode)
  const cityName = nameof((x) => x.city)
  const cityPartName = nameof((x) => x.cityPart)
  const isCatastralNumberName = nameof((x) => x.isCatastralNumber)
  const postCodeCuName = nameof((x) => x.postCodeCu)
  const catastralAreaCodeName = nameof((x) => x.catastralAreaCode)
  const parcelNumberName = nameof((x) => x.parcelNumber)
  const isEvidenceNumberName = nameof((x) => x.isEvidenceNumber)
  const withoutStreetName = nameof((x) => x.withoutStreet)
  const parcelNumberTypeAvailableName = nameof((x) => x.parcelNumberTypeAvailable)
  const parcelNumberTypeName = nameof((x) => x.parcelNumberType)

  const { error: addressPointErrors } = getFieldState(addressPointName)
  const { error: streetNumberErrors } = getFieldState(streetNumberName)
  const { error: postCodeErrors } = getFieldState(postCodeName)
  const { error: cityWithCityPartErrors } = getFieldState(cityWithCityPartName)
  const { error: postCodeCuErrors } = getFieldState(postCodeCuName)
  const { error: catastralAreaCodeErrors } = getFieldState(catastralAreaCodeName)
  const { error: parcelNumberErrors } = getFieldState(parcelNumberName)
  const { error: parcelNumberTypeErrors } = getFieldState(parcelNumberTypeName)

  const street = watch(streetName)
  const streetNumber = watch(streetNumberName)
  const onlyLotNumberValue = watch(isCatastralNumberName)
  const katUzemi = watch(postCodeCuName)
  const isEvidenceNumber = watch(isEvidenceNumberName)
  const oddeleneRady = watch(parcelNumberTypeAvailableName)

  const { refetch: refetchStreets } = useFindStreet(street, { enabled: false })
  const { refetch: refetchAddress } = useFindAddress(street, streetNumber, { enabled: false })

  const [validKatUzemiPostCode, setValidKatUzemiPostCode] = useState(false)
  const { data: katUzemiData } = useFindKatUzemi(katUzemi, { enabled: validKatUzemiPostCode })
  const katUzemiDataSource = validKatUzemiPostCode ? (katUzemiData ?? []) : []

  const handleCatastralAreaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setValue(parcelNumberTypeAvailableName, undefined)
    setValue(parcelNumberTypeName, undefined)
    if (event.target.value && katUzemiData) {      
      const item = katUzemiData.find(k => k.id === Number(event.target.value))
      if (item) {
        setValue(cityName, item.obecNazev)
        setValue(parcelNumberTypeAvailableName, item.oddeleneRady)
      }
    }
  }

  useEffect(() => {
    setValidKatUzemiPostCode(katUzemi?.length == 5 && !postCodeCuErrors)
  }, [katUzemi, postCodeCuErrors])

  return (
    <>
      <SwitchControl>
        <Switch
          id="onlyLotNumber"
          isDisabled={isDisabled}
          {...register(isCatastralNumberName)}
          isChecked={watch(isCatastralNumberName)}
        />
        <FormLabel htmlFor="onlyLotNumber" variant="switch">
          Mám pouze parcelní číslo
        </FormLabel>
      </SwitchControl>

      {!onlyLotNumberValue && (
        <>
          <FormGroup>
            <FormControl isRequired isInvalid={!!addressPointErrors}>
              <FormLabel>Ulice</FormLabel>

              <Autocomplete
                inputMode="text"
                htmlSize={32}
                validFromLength={2}
                value={street ?? ''}
                placeholder="Např. Hlavní"
                isDisabled={isDisabled}
                initLoad={!!street}
                errors={addressPointErrors}
                onChange={(value) => {
                  setValue(streetName, value)

                  if (value != street) {
                    setValue(streetNumberName, '')
                    setValue(cityWithCityPartName, '')
                    setValue(postCodeName, '')
                    setValue(cityName, '')
                    setValue(cityPartName, '')
                    setValue(addressPointName, null)
                    setValue(withoutStreetName, false)
                  }
                }}
                refetch={() => {
                  return new Promise((resolve, reject) => {
                    refetchStreets()
                      .then(({ data }) =>
                        resolve(data?.map((x) => ({ inputText: x, value: x })) ?? []),
                      )
                      .catch((e) => reject(e))
                  })
                }}
              />
            </FormControl>

            <FormControl isRequired isInvalid={!!streetNumberErrors}>
              <FormLabel>
                {isEvidenceNumber ? 'Číslo evidenční' : 'Číslo popisné nebo orientační'}
              </FormLabel>

              <Autocomplete
                inputMode="text"
                htmlSize={8}
                value={streetNumber ?? ''}
                placeholder="Např. 1234"
                isDisabled={isDisabled}
                errors={streetNumberErrors}
                initLoad={!!street}
                onChange={(value) => {
                  setValue(streetNumberName, value)
                }}
                onSelect={(item) => {
                  const adresa = item.customData as FindAddressResult

                  const newStreetNumber = adresa.cisloOrientacni
                    ? `${adresa.cisloDomovni}/${adresa.cisloOrientacni}`
                    : adresa.cisloDomovni

                  let cityWithPart = adresa.obec
                  if (adresa.castObce && adresa.castObce !== adresa.obec)
                    cityWithPart += ' - ' + adresa.castObce

                  setValue(streetName, adresa.ulice ?? '')
                  setValue(streetNumberName, newStreetNumber ?? '')
                  setValue(cityWithCityPartName, cityWithPart)
                  setValue(postCodeName, adresa.psc)
                  setValue(cityName, adresa.obec)
                  setValue(cityPartName, adresa.castObce ?? '')
                  setValue(
                    addressPointName,
                    (adresa.adresniMisto ?? '').toString().trim() == ''
                      ? null
                      : adresa.adresniMisto,
                  ) // obcas to padalo na chybu (nepodarilo se nasimulovat), tak preventive rusim moznost prazdenoho stringu - "property.data.address.addressPoint must be a `number` type, but the final value was: `NaN` (cast from the value `""`)."
                  setValue(isEvidenceNumberName, adresa.typSo == 'č.ev.')
                  setValue(withoutStreetName, !adresa.ulice)
                }}
                refetch={() => {
                  return new Promise((resolve, reject) => {
                    refetchAddress()
                      .then(({ data }) =>
                        resolve(
                          (data ?? [])
                            .sort((x, y) => x.obec.localeCompare(y.obec))
                            .map((x) => ({
                              inputText: x.cisloDomovni ?? x.cisloDomovni ?? '',
                              itemText: x.adresa,
                              value: x.adresniMisto?.toString() ?? '',
                              customData: x,
                            })),
                        ),
                      )
                      .catch((e) => reject(e))
                  })
                }}
              />
            </FormControl>
          </FormGroup>

          <FormGroup>
            <FormControl isInvalid={!!postCodeErrors}>
              <FormLabel tooltip="Zadejte poštovní směrovací číslo obce. Údaj lze nalézt na webových stránkách www.psc.cz.">
                PSČ
              </FormLabel>

              <InputGroup
                htmlSize={6}
                autoComplete="postal-code"
                placeholder="Např. 110 00"
                isDisabled={true}
                {...register(postCodeName)}
                errors={postCodeErrors}
              />
            </FormControl>

            <FormControl isInvalid={!!cityWithCityPartErrors}>
              <FormLabel tooltip="Zadejte obec">Obec</FormLabel>

              <InputGroup {...register(cityWithCityPartName)} isDisabled={true} />

              {cityWithCityPartErrors && (
                <FormErrorMessage>{cityWithCityPartErrors.message}</FormErrorMessage>
              )}
            </FormControl>
          </FormGroup>
        </>
      )}

      {onlyLotNumberValue && (
        <>
          <FormGroup>
            <FormControl isInvalid={!!postCodeCuErrors}>
              <FormLabel tooltip="Zadejte poštovní směrovací číslo obce. Údaj lze nalézt na webových stránkách www.psc.cz.">
                PSČ
              </FormLabel>

              <InputGroup
                htmlSize={6}
                autoComplete="postal-code"
                placeholder="Např. 110 00"
                isDisabled={isDisabled}
                {...register(postCodeCuName)}
                errors={postCodeCuErrors}
              />
            </FormControl>

            <FormControl isInvalid={!!catastralAreaCodeErrors}>
              <FormLabel tooltip="Katastrální území">Katastrální území</FormLabel>

              {(katUzemiDataSource?.length ?? 0) > 0 && (
                <Select {...register(catastralAreaCodeName)} isDisabled={isDisabled} onChange={handleCatastralAreaChange}>
                  <option disabled value="">
                    Vyberte katastrální území
                  </option>
                  {katUzemiDataSource.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.nazev}
                    </option>
                  ))}
                </Select>
              )}
              {(katUzemiDataSource?.length ?? 0) == 0 && (
                <Select isDisabled={true} defaultValue="">
                  <option disabled value="">
                    Vyberte katastrální území
                  </option>
                </Select>
              )}

              {catastralAreaCodeErrors && (
                <FormErrorMessage>{catastralAreaCodeErrors.message}</FormErrorMessage>
              )}
            </FormControl>
          </FormGroup>

          <FormGroup>

            {oddeleneRady && (
              <FormControl isInvalid={!!parcelNumberTypeErrors}>
              <FormLabel>Typ parcely</FormLabel>

              <Select {...register(parcelNumberTypeName)} 
                value={watch(parcelNumberTypeName)?.toString() ?? ''}
                onChange={evnt => setValue(parcelNumberTypeName, evnt.target.value ? Number(evnt.target.value) : undefined)}
                isDisabled={isDisabled}>
                <option disabled value="">
                  Vyberte typ parcely
                </option>                 
                <option value={'1'} key={1}>
                  Stavební
                </option>
                <option value={'2'} key={2}>
                  Pozemková
                </option>                
              </Select>
            
              {parcelNumberTypeErrors && (
                <FormErrorMessage>{parcelNumberTypeErrors.message}</FormErrorMessage>
              )}
            </FormControl>
            )}

            <FormControl isInvalid={!!parcelNumberErrors}>
              <FormLabel>Parcelní číslo</FormLabel>

              <InputGroup
                htmlSize={32}
                {...register(parcelNumberName)}
                errors={parcelNumberErrors}
                isDisabled={isDisabled}
              />
            </FormControl>
          </FormGroup>
        </>
      )}
    </>
  )
}

export default RuianAddressSelector
